import { NavLink } from 'react-router-dom';
import {
  BlacklistWarningItem,
  ButtonPseudo,
  ButtonSecondary,
  ButtonSize,
  IconAttention,
  IconBlock,
  IconChangeLoClock,
  IconLoader,
  IconSandWatch,
  IconSelected,
  PermissionEnum,
} from '@ui/src';
import { NewApplicationStatus } from '../../../../../hoc/new-application/types';
import { OngoingStatus } from '../../../../../hoc/common/types';
import { OngoingMonitoringContentTypes } from '../types';

import cn from '../styles.module.css';

const OngoingMonitoringContent = ({
  isAllowed,
  isLoading,
  status,
  ongoingCheckingStatus,
  restartOngoingCheckingAction,
  restartInProgress,
  ongoingMonitoring,
  ongoingWithoutActions,
  activePermissions,
  setApplyAllPopupVisible,
  currentEmployee,
  applyOngoingWarning,
  setBlacklistDataVisible,
  setBlacklistItemVisible,
  applyWarningInProgress,
  setBlacklistData,
  setBlacklistDataItem,
  onResetTransactionWarningAction,
  resetTransactionsWarningIsLoading,
  resetTransactionsWarningIsSuccess,
  selectedWarningId,
  getOngoingWarningById,
  isWarningByIdFetching,
  isWarningByIdLoading,
  getApplicationWarningsAction,
  selectedWarningGroup,
  paginationInProgress,
  applyError,
}: OngoingMonitoringContentTypes) => {
  let content;

  if (!isAllowed) {
    content = (
      <div className={cn.waitingStatus}>
        <IconAttention className={cn.redIcon} />
        You dont have access to this section
      </div>
    );
  } else if (isAllowed) {
    if (isLoading) {
      content = (
        <div className={cn.ongoingLoader}>
          <IconLoader />
        </div>
      );
    } else if (!isLoading) {
      if (status === NewApplicationStatus.INCOMPLETE) {
        content = (
          <div className={cn.waitingStatus}>
            Blacklist Checking will start with the “Waiting for review” status
          </div>
        );
      } else if (
        ongoingCheckingStatus?.status === OngoingStatus.FAIL ||
        !ongoingCheckingStatus
      ) {
        content = (
          <div className={cn.waitingStatus}>
            <IconBlock className={cn.blockIcon} />
            An error occurred while checking
            <ButtonSecondary
              onClick={restartOngoingCheckingAction}
              size={ButtonSize.SMALL}
              text={restartInProgress ? 'Loading...' : 'Try again'}
              className={cn.restartButton}
              disabled={restartInProgress}
            />
          </div>
        );
      } else if (
        ongoingCheckingStatus?.status === OngoingStatus.QUEUED ||
        ongoingCheckingStatus?.status === OngoingStatus.PROCESSING
      ) {
        content = (
          <div className={cn.waitingStatus}>
            <IconSandWatch className={cn.iconSandWatch} />
            Checking for sanctions lists...
          </div>
        );
      } else if (
        ongoingMonitoring?.length &&
        ongoingMonitoring[0]?.warnings?.edges?.length
      ) {
        content = (
          <div className={cn.ongoingWrapper}>
            {ongoingWithoutActions?.length > 0 && (
              <>
                <div className={cn.ongoingTop}>
                  <IconAttention className={cn.redIcon} />
                  Sanction list matching detected
                  {activePermissions?.includes(
                    PermissionEnum.AUDIT_LOG_LIST,
                  ) && (
                    <NavLink
                      to={`/administration/change-logs?entityType=WARNING_GROUP&entityId=${ongoingMonitoring[0].id}`}
                      className={cn.changeLogLink}
                      target="_blank"
                    >
                      <IconChangeLoClock />
                      Change Log
                    </NavLink>
                  )}
                </div>
                <div className={cn.ongoingActions}>
                  {(activePermissions?.includes(
                    PermissionEnum.APPLICATIONS_CUSTOMERS_AML_APPROVE_ACTION,
                  ) ||
                    activePermissions?.includes(
                      PermissionEnum.APPLICATIONS_CUSTOMERS_AML_REJECT_ACTION,
                    )) && (
                    <ButtonSecondary
                      size={ButtonSize.SMALL}
                      text="Apply to all matches"
                      className={`button-white ${cn.applyAll}`}
                      onClick={() => setApplyAllPopupVisible(true)}
                    />
                  )}
                </div>
              </>
            )}

            {ongoingMonitoring?.map((item) => (
              <div key={item.id}>
                {item?.warnings?.edges?.map((warning) => (
                  <BlacklistWarningItem
                    key={warning.node.id}
                    warning={warning.node}
                    employee={currentEmployee}
                    onApplyTransactionAction={(data) => {
                      applyOngoingWarning({
                        id: item.id,
                        warningId: data.warningId,
                        action: data.body.action,
                        comment: data.body.comment,
                        matchStatus: data.body.matchStatus,
                      });
                    }}
                    setBlacklistDataVisible={() => {
                      setBlacklistDataVisible(true);
                    }}
                    setBlacklistItemVisible={() => {
                      setBlacklistItemVisible(true);
                    }}
                    applyTransactionsWarningIsLoading={applyWarningInProgress}
                    setBlacklistData={(data) => setBlacklistData(data)}
                    setBlacklistDataItem={(data) => setBlacklistDataItem(data)}
                    activePermissions={activePermissions}
                    hideApproveActions={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_AML_APPROVE_ACTION,
                      )
                    }
                    hideRejectActions={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_AML_REJECT_ACTION,
                      )
                    }
                    onResetTransactionWarningAction={(warningId) => {
                      if (onResetTransactionWarningAction) {
                        onResetTransactionWarningAction(warningId, item.id);
                      }
                    }}
                    resetTransactionsWarningIsLoading={
                      resetTransactionsWarningIsLoading
                    }
                    resetTransactionsWarningIsSuccess={
                      resetTransactionsWarningIsSuccess
                    }
                    warningInGroupId={selectedWarningId}
                    getWarningById={getOngoingWarningById}
                    entityId={item.id}
                    isWarningByIdFetching={isWarningByIdFetching}
                    isWarningByIdLoading={isWarningByIdLoading}
                    globalCollapsed
                    isPaymentOrder
                    withoutChat
                  />
                ))}
                {getApplicationWarningsAction &&
                  item?.warnings?.pageInfo.hasNextPage && (
                    <div className={cn.warningsGroupPagination}>
                      <ButtonPseudo
                        text={
                          selectedWarningGroup === item.id &&
                          paginationInProgress
                            ? 'Loading...'
                            : 'Show more'
                        }
                        disabled={paginationInProgress}
                        onClick={() =>
                          getApplicationWarningsAction({
                            warningGroupId: item.id,
                            cursor: item?.warnings?.pageInfo?.endCursor,
                          })
                        }
                        bold
                      />
                    </div>
                  )}
              </div>
            ))}
            {applyError && <div className={cn.applyError}>{applyError}</div>}
            {ongoingMonitoring &&
              ongoingMonitoring[0] &&
              ongoingMonitoring[0]?.warningsGroupsBlacklists?.edges?.length >
                0 && (
                <div className={cn.checkedBlockLists}>
                  <div className={cn.checkedBlacklistTitle}>
                    Checked blocklists
                  </div>
                  <ul className={cn.blacklists}>
                    {ongoingMonitoring[0]?.warningsGroupsBlacklists?.edges?.map(
                      (item) => (
                        <li key={item.node.id}>{item.node.blacklist.name}</li>
                      ),
                    )}
                  </ul>
                </div>
              )}
          </div>
        );
      } else if (
        !ongoingMonitoring?.length ||
        (ongoingMonitoring && !ongoingMonitoring[0]?.warnings?.edges?.length)
      ) {
        content = (
          <>
            <div className={cn.waitingStatus}>
              <IconSelected /> No matches found
            </div>

            {ongoingMonitoring &&
              ongoingMonitoring[0] &&
              ongoingMonitoring[0]?.warningsGroupsBlacklists?.edges?.length >
                0 && (
                <div className={cn.checkedBlockLists}>
                  <div className={cn.checkedBlacklistTitle}>
                    Checked blocklists
                  </div>
                  <ul className={cn.blacklists}>
                    {ongoingMonitoring[0]?.warningsGroupsBlacklists?.edges?.map(
                      (item) => (
                        <li key={item.node.id}>{item.node.blacklist.name}</li>
                      ),
                    )}
                  </ul>
                </div>
              )}
          </>
        );
      }
    }
  }

  return content;
};

export default OngoingMonitoringContent;
